import { t } from 'i18next'
import { useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import {
  useAlgorithmRunDetails,
  useWorkspaceDetails,
  useWorkspaceSmartFunctionData,
} from '.'
import {
  algorithmRunStatuses,
  NOTIFICATION_DEFAULT_DURATION,
  ongoingStatuses,
  riskReliefSmartFunctionTypes,
  workspaceDocumentSetStatuses,
  workspaceTypes,
} from '../constants/constants'
import { routePaths } from '../pages/RootPage'
import { ExtendedAlgorithmRun } from '../pages/WorkspaceDetailsPage/panels/WorkspaceSmartFunctionPanel'
import {
  notificationService,
  smartFunctionActionService,
  smartFunctionService,
} from '../services'
import {
  AlgorithmRun,
  AlgorithmRunConfig,
  AlgorithmRunRiskReliefConfig,
  NotifyUsersOfSmartFunctionResultsBody,
  RiskReliefAlgorithmRun,
  setSelectedAlgorithmRun,
} from '../store/reducers/workspaceReducer'

interface UseSmartFunctionActionsProps {
  workspaceId?: string
  algorithmRun?: RiskReliefAlgorithmRun | AlgorithmRun
  algorithmRunId?: string
}

export enum SmartFunctionActionDropdownKeys {
  RUN_ALGORITHM = 'RUN_ALGORITHM',
  RESUME_RUN = 'RESUME_RUN',
  CANCEL_RUN = 'CANCEL_RUN',
  BIOMARK_CONFIGURATION = 'BIOMARK_CONFIGURATION',
  REGSUB_CONFIGURATION = 'REGSUB_CONFIGURATION',
  DEL_CONFIGURATION = 'DEL_CONFIGURATION',
  COMPENSATION_MATRIX_CONFIGURATION = 'COMPENSATION_MATRIX_CONFIGURATION',
  REVIEW_ANALYSIS_CONFIGURATION = 'REVIEW_ANALYSIS_CONFIGURATION',
  VIEW_BIOMARK_RUN_SETUP = 'VIEW_BIOMARK_RUN_SETUP',
  VIEW_REGSUB_RUN_SETUP = 'VIEW_REGSUB_RUN_SETUP',
  VIEW_DEL_RUN_SETUP = 'VIEW_DEL_RUN_SETUP',
  COMPENSATION_MATRIX_RUN_SETUP = 'COMPENSATION_MATRIX_RUN_SETUP',
  EXPORT_RUN = 'EXPORT_RUN',
  PRINT_CONTENT = 'PRINT_CONTENT',
  RISK_RELIEF_CONFIGURATION = 'RISK_RELIEF_CONFIGURATION',
  OFR_QR = 'OFR_QR',
}

export interface MenuItemHandlers {
  handleAlgorithmRunClick: () => void
  handleCancelRunClick: () => void
  handleExportRun: () => void
  handlePrintContent: () => void
}

export const useSmartFunctionActions = ({
  workspaceId,
  algorithmRun,
  algorithmRunId,
}: UseSmartFunctionActionsProps) => {
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { deviationId, documentId } = useParams()

  const { calculateStatusLabQr } = useWorkspaceSmartFunctionData()

  const { selectedWorkspace, refreshWorkspaceDetails, exportSmartFunctionCsv } =
    useWorkspaceDetails({
      workspaceId,
      preventFetch: true,
    })

  const { refreshAlgorithmRun, runAlgorithm, cancelAlgorithmRun } =
    useAlgorithmRunDetails({
      algorithmRunId,
      workspaceId,
      preventFetch: true,
    })

  const getBaseRoute = (typeCode?: string, pathname?: string) => {
    switch (typeCode) {
      case workspaceTypes.OFR_QR:
        return routePaths.WORKSPACE_SMART_FUNCTION_DETAILS_OFR_QR
      case workspaceTypes.BIOMARK:
        return routePaths.WORKSPACE_SMART_FUNCTION_DETAILS_BIOMARK
      case workspaceTypes.REGSUB:
        return routePaths.WORKSPACE_SMART_FUNCTION_DETAILS_REGSUB
      case workspaceTypes.DEL:
        return routePaths.WORKSPACE_SMART_FUNCTION_DETAILS_DEL
      case workspaceTypes.RISK_REL:
        return routePaths.WORKSPACE_SMART_FUNCTION_DETAILS_RISK_REL_DEVIATION_CLASSIFICATION
      case workspaceTypes.REFAI:
        return pathname?.includes(
          routePaths.SINGLE_WORKSPACE_SMART_FUNCTION_DETAILS_REFAI
        )
          ? routePaths.SINGLE_WORKSPACE_SMART_FUNCTION_DETAILS_REFAI
          : routePaths.WORKSPACE_SMART_FUNCTION_DETAILS_REFAI
      default:
        return ''
    }
  }

  const isRiskReliefPage = (pathname: string, deviationId?: string) => {
    return (
      pathname.includes(
        routePaths.WORKSPACE_SMART_FUNCTION_DETAILS_RISK_REL_DEVIATION_CLASSIFICATION
      ) ||
      pathname.includes(
        routePaths.WORKSPACE_SMART_FUNCTION_DETAILS_RISK_REL_ROOT_CAUSE_DISCOVERY
      ) ||
      (pathname.includes(
        routePaths.WORKSPACE_SMART_FUNCTION_DETAILS_RISK_REL_CAPA_GENERATION
      ) &&
        deviationId)
    )
  }

  const isConformityCheckQRPage = (pathname: string, documentId?: string) => {
    return (
      pathname.includes(routePaths.WORKSPACE_SMART_FUNCTION_DETAILS_OFR_QR) &&
      documentId
    )
  }

  interface NavigatePathProps {
    pathname: string
    typeCode: string
    workspaceId: string
    responseId: string
    deviationId?: string
    documentId?: string
  }

  const getNavigatePath = useCallback(
    ({
      pathname,
      typeCode,
      workspaceId,
      responseId,
      deviationId,
      documentId,
    }: NavigatePathProps) => {
      const baseRoute = getBaseRoute(typeCode, pathname)

      if (isRiskReliefPage(pathname, deviationId)) {
        return `${baseRoute}/${workspaceId}/${responseId}/${deviationId}`
      }

      if (isConformityCheckQRPage(pathname, documentId)) {
        return `${baseRoute}/${workspaceId}/${responseId}/${documentId}`
      }

      return `${baseRoute}/${workspaceId}/${responseId}`
    },
    []
  )

  const handleAlgorithmRunClick = useCallback(async () => {
    if (!algorithmRun || !selectedWorkspace) return

    const { typeCode, id } = selectedWorkspace

    try {
      const config = await smartFunctionActionService.getAlgorithmConfig({
        typeCode,
        workspace: selectedWorkspace,
        searchQueryId: deviationId,
        documentObjectId: documentId,
        algorithmRun,
      })

      const response = await runAlgorithm(workspaceId || '', config)

      const shouldNavigate = !location.pathname.includes(
        routePaths.WORKSPACE_DETAILS
      )

      if (shouldNavigate) {
        const path = getNavigatePath({
          pathname: location.pathname,
          typeCode,
          workspaceId: id,
          responseId: response.id,
          deviationId,
          documentId,
        })
        navigate(path)
      } else {
        refreshWorkspaceDetails()
      }

      notificationService.notificationSuccess({
        messageToDisplay: t('smartFunctionDetailsPage.runStarted'),
      })
    } catch (error) {
      console.error('Error running algorithm:', error)
      notificationService.notificationError({
        messageToDisplay: t('smartFunctionDetailsPage.runFailed'),
      })
    } finally {
      refreshWorkspaceDetails()
      refreshAlgorithmRun()
    }
  }, [
    algorithmRun,
    selectedWorkspace,
    deviationId,
    documentId,
    runAlgorithm,
    workspaceId,
    location,
    getNavigatePath,
    navigate,
    refreshWorkspaceDetails,
    refreshAlgorithmRun,
  ])

  const handleCancelRunClick = useCallback(async () => {
    try {
      await cancelAlgorithmRun(
        selectedWorkspace?.id || '',
        algorithmRun?.id || '',
        navigate
      )
    } catch (error) {
      console.error('Error cancelling run:', error)
    } finally {
      refreshWorkspaceDetails()
      refreshAlgorithmRun()
    }
  }, [
    selectedWorkspace,
    algorithmRun,
    navigate,
    refreshWorkspaceDetails,
    refreshAlgorithmRun,
    cancelAlgorithmRun,
  ])

  const handleExportRun = useCallback(() => {
    if (selectedWorkspace && algorithmRun) {
      exportSmartFunctionCsv(selectedWorkspace.id, algorithmRun.id)
    }
  }, [selectedWorkspace, algorithmRun, exportSmartFunctionCsv])

  const handlePrintContent = useCallback(() => {
    window.print()
  }, [])

  const isRiskReliefInclusionValid = (
    config: AlgorithmRunRiskReliefConfig
  ): boolean => {
    const inclusions = config?.rootCauseInclusions
    return !!inclusions && Object.keys(inclusions).length > 0
  }

  const isOfrQrRunAvailable = useCallback(
    (run: ExtendedAlgorithmRun): boolean => {
      const documentSetStatus =
        selectedWorkspace?.config?.conformityChecksConfig?.documents?.find(
          (doc) => doc.id === documentId
        )?.documentSetStatus

      if (documentSetStatus === workspaceDocumentSetStatuses.CLOSED) {
        return false
      }
      const status =
        run?.calculatedStatus ??
        calculateStatusLabQr({
          status: run?.nodeStatuses?.LABQR,
          runs: [run],
        })

      return !ongoingStatuses.includes(status!)
    },
    [calculateStatusLabQr, selectedWorkspace, documentId]
  )

  interface ResumeSmartFunctionNodeProps {
    workspaceId: string
    runId: string
    nodeId: string
  }
  const resumeSmartFunctionNode = useCallback(
    async ({ workspaceId, runId, nodeId }: ResumeSmartFunctionNodeProps) => {
      try {
        if (!selectedWorkspace || !algorithmRun) return
        const config = await smartFunctionActionService.getAlgorithmConfig({
          typeCode: selectedWorkspace?.typeCode || '',
          workspace: selectedWorkspace,
          searchQueryId: deviationId,
          documentObjectId: documentId,
          algorithmRun,
        })
        if (!config) return

        if (
          selectedWorkspace?.typeCode === workspaceTypes.RISK_REL &&
          (algorithmRun as RiskReliefAlgorithmRun)?.smartFunctionType ===
            riskReliefSmartFunctionTypes.CAPA_GENERATION
        ) {
          if (
            !isRiskReliefInclusionValid(config as AlgorithmRunRiskReliefConfig)
          ) {
            notificationService.notificationWarning({
              messageToDisplay: t(
                'smartFunctionDetailsPage.riskReliefRootCause.noInclusions'
              ),
              duration: NOTIFICATION_DEFAULT_DURATION,
              closable: true,
            })
            return
          }
        }

        const response = await smartFunctionService.resumeSmartFunctionNode({
          workspaceId,
          runId,
          nodeId,
          navigate,
          config: config as AlgorithmRunConfig,
        })

        if (response) {
          dispatch(setSelectedAlgorithmRun(response.data))
          refreshWorkspaceDetails()
          refreshAlgorithmRun()
        }
      } catch (error) {
        console.error('Error resuming CAPA generation:', error)
      }
    },
    [
      selectedWorkspace,
      algorithmRun,
      deviationId,
      documentId,
      navigate,
      dispatch,
      refreshWorkspaceDetails,
      refreshAlgorithmRun,
    ]
  )

  const isRiskReliefRunAvailable = (run?: RiskReliefAlgorithmRun): boolean => {
    if (!run) return false

    const { smartFunctionType, nodeStatuses, runStatus } = run

    const isAnyNodeOngoing = () => {
      // check if any node is ongoing
      return (
        nodeStatuses?.DEVIATION_CLASSIFICATION ===
          algorithmRunStatuses.ONGOING_INGESTION ||
        nodeStatuses?.DEVIATION_CLASSIFICATION ===
          algorithmRunStatuses.ONGOING ||
        nodeStatuses?.ROOT_CAUSE_DISCOVERY === algorithmRunStatuses.ONGOING ||
        nodeStatuses?.CAPA_GENERATION === algorithmRunStatuses.ONGOING
      )
    }

    const areDcAndRcdCompleted = () => {
      return (
        nodeStatuses?.DEVIATION_CLASSIFICATION ===
          algorithmRunStatuses.COMPLETE &&
        nodeStatuses?.ROOT_CAUSE_DISCOVERY === algorithmRunStatuses.COMPLETE
      )
    }

    const isRcdReady = () => {
      return (
        nodeStatuses?.ROOT_CAUSE_DISCOVERY === algorithmRunStatuses.COMPLETE ||
        nodeStatuses?.ROOT_CAUSE_DISCOVERY !== algorithmRunStatuses.FAILED ||
        nodeStatuses?.ROOT_CAUSE_DISCOVERY !== algorithmRunStatuses.CANCELLED
      )
    }

    if (
      smartFunctionType ===
      riskReliefSmartFunctionTypes.DEVIATION_CLASSIFICATION
    ) {
      if (isAnyNodeOngoing()) return false
      // DC Run Algorithm is enabled when DC & RCD are completed and CG is paused or if DC is completed and RCD is not ongoing
      return areDcAndRcdCompleted() || isRcdReady()
    }

    if (
      smartFunctionType === riskReliefSmartFunctionTypes.ROOT_CAUSE_DISCOVERY
    ) {
      // RCD Run Algorithm is disabled when any node is ongoing
      if (isAnyNodeOngoing()) return false
      // RCD Run Algorithm is disabled at all times (when RCD is Ready) if CG is paused
      return false
    }
    if (smartFunctionType === riskReliefSmartFunctionTypes.CAPA_GENERATION) {
      if (isAnyNodeOngoing()) return false
      if (areDcAndRcdCompleted()) {
        if (runStatus !== algorithmRunStatuses.PAUSED) return false
        return true
      }
      return runStatus === algorithmRunStatuses.PAUSED
    }

    return false
  }

  interface NotifyUsersOfSmartFunctionResultsProps {
    body: NotifyUsersOfSmartFunctionResultsBody
  }

  const notifyUsersOfSmartFunctionResults = useCallback(
    async ({ body }: NotifyUsersOfSmartFunctionResultsProps) => {
      if (!selectedWorkspace || !algorithmRunId) return
      await smartFunctionService.notifyUsersOfSmartFunctionResults({
        workspaceId: selectedWorkspace.id,
        algorithmRunId,
        body,
        navigate,
      })
    },
    [selectedWorkspace, algorithmRunId, navigate]
  )

  return {
    handleAlgorithmRunClick,
    handleCancelRunClick,
    handleExportRun,
    handlePrintContent,
    resumeSmartFunctionNode,
    isRiskReliefRunAvailable,
    isOfrQrRunAvailable,
    notifyUsersOfSmartFunctionResults,
  }
}

export default useSmartFunctionActions
